import { apiGet } from '../../generic/Api_Functions'
import downloadAsCsv from '../csv_utils'
import { serializeCsvArrayCell } from '../../generic/csvExportSerializer'

const EXPORT_PAGE_SIZE = 100

/**
 * Asks `dataFetch` again for every row matching the current query, in pages of 100, as the
 * export of `generic/SmartMateriaUIToolbar.jsx` does for a pageable table.
 * @param {Function} dataFetch The table's data fetch function
 * @param {Object} requestPagination The query the table is showing
 * @param {Function} onProgress Called with the percentage fetched so far
 * @returns {Promise<Array<Object>>}
 */
const fetchAllRows = async (dataFetch, requestPagination, onProgress) => {
  const requestPaginationCopy = structuredClone(requestPagination)
  let allContent = []
  let page = 0
  let totalPages = null
  requestPaginationCopy.size = EXPORT_PAGE_SIZE

  while (totalPages === null || page < totalPages) {
    requestPaginationCopy.page = page
    const response = await dataFetch(requestPaginationCopy)
    if (totalPages === null) {
      totalPages = Math.ceil(response.totalElements / EXPORT_PAGE_SIZE)
    }
    allContent = allContent.concat(response.content)
    page++
    onProgress(Math.floor((page / totalPages) * 100))
  }
  return allContent
}

/**
 * One exported cell, walked from the raw row the way today's export does it (a path segment that
 * is not in the object leaves the walk where it was).
 */
const toCsvCell = (line, column, csvSeparator, formatDateTime) => {
  // A column whose exported value is not its `field` walked on the row declares `csvValue(row)` (the
  // "Required date" column keeps its historical field but exports another value of the row).
  if (typeof column.csvValue === 'function') {
    const value = column.csvValue(line)
    return value === null || value === undefined ? '' : value
  }
  let str = column.field
  let obj = line
  str = str.replace(/\[(\w+)\]/g, '.$1')
  str = str.replace(/^\./, '')
  const pathSegments = str.split('.')
  for (let i = 0, n = pathSegments.length; i < n; ++i) {
    const key = pathSegments[i]
    if (obj !== null && obj !== undefined && obj !== 'undefined' && key in obj) {
      obj = obj[key]
    }
  }
  if (obj == null || obj === '') {
    return ''
  }
  if (Array.isArray(obj)) {
    return serializeCsvArrayCell(obj, csvSeparator)
  }
  if (column.type === 'datetime' || column.type === 'dateTimePicker' || column.type === 'datePicker' || column.type === 'time') {
    return formatDateTime(obj)
  }
  return obj
}

/**
 * Exports every declared column (not only the ones shown by TUNE) of every matching row to a CSV
 * file named after the table title, or `temp` without one.
 * @param {Object} params
 * @param {Function} params.dataFetch
 * @param {Object} params.requestPagination
 * @param {Array<Object>} params.columns Declared columns
 * @param {String} params.title
 * @param {Function} params.formatDateTime
 * @param {Function} params.onProgress
 */
const exportDetailsTableCsv = async ({ dataFetch, requestPagination, columns, title, formatDateTime, onProgress }) => {
  const allContent = await fetchAllRows(dataFetch, requestPagination, onProgress)
  const csvSeparator = await apiGet('settings/character').then((result) => {
    if (result[0].length !== 0) {
      return result[0]
    }
  })

  const filteredColumns = columns.filter(column => column.field !== undefined && column.field !== null && column.field !== '')
  const data = allContent.map(line => filteredColumns.map(column => toCsvCell(line, column, csvSeparator, formatDateTime)))
  downloadAsCsv(filteredColumns, data, title || 'temp', csvSeparator)
}

export default exportDetailsTableCsv
